import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SleekButton from "../../components/ui/NavButtons";

const SOLUTIONS = [
  {
    module: "PortfolioIQ",
    moduleColor: "dark:text-emerald-400 text-emerald-600",
    moduleBg: "dark:bg-emerald-500/10 bg-emerald-100 dark:border-emerald-500/20 border-emerald-200",
    problem: "Night before a lender meeting",
    outcome: "Generate a lender-ready DSCR report in one click.",
    detail: "Formatted PDF with your property financials, rent rolls, and coverage ratios. Ready in 60 seconds.",
    metric: "60 sec",
    metricLabel: "to generate",
    accent: "emerald",
  },
  {
    module: "WealthScope",
    moduleColor: "dark:text-blue-400 text-blue-600",
    moduleBg: "dark:bg-blue-500/10 bg-blue-100 dark:border-blue-500/20 border-blue-200",
    problem: "Before making an offer",
    outcome: "Know your exact buying power and deal readiness.",
    detail: "Your net worth, DTI ratio, and Deal Readiness Score update in real time as your accounts change.",
    metric: "76/100",
    metricLabel: "deal readiness example",
    accent: "blue",
  },
  {
    module: "PortfolioIQ",
    moduleColor: "dark:text-emerald-400 text-emerald-600",
    moduleBg: "dark:bg-emerald-500/10 bg-emerald-100 dark:border-emerald-500/20 border-emerald-200",
    problem: "Tax season",
    outcome: "Send your CPA a clean Schedule E in one export.",
    detail: "Rental income, repairs, depreciation, and mortgage interest per property — auto-calculated. No spreadsheet digging.",
    metric: "1-click",
    metricLabel: "Schedule E export",
    accent: "emerald",
  },
];

export const SolutionSection: React.FC = () => {
  return (
    <section className="relative w-full px-4 sm:px-6 py-20 overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 right-0 translate-x-1/4 w-[500px] h-[500px] dark:bg-indigo-600/10 bg-blue-400/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-[1100px] mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-5">
            The Fix
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold dark:text-white text-gray-900 mb-4 leading-tight">
            Every problem above has a{" "}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">specific answer</span>{" "}
            in VaWeNa.
          </h2>
          <p className="text-base dark:text-slate-400 text-gray-500 max-w-lg mx-auto leading-relaxed">
            Not another dashboard. Not more data to interpret. Specific outputs you can act on — in seconds.
          </p>
        </div>

        {/* Solution rows */}
        <div className="space-y-5 mb-16">
          {SOLUTIONS.map((s, i) => (
            <motion.div
              key={s.outcome}
              initial={{ opacity: 0, x: i % 2 === 0 ? -24 : 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-5 dark:bg-white/[0.03] bg-white border dark:border-white/[0.07] border-blue-300/80 rounded-2xl p-5 sm:p-6 hover:-translate-y-0.5 hover:dark:bg-white/[0.05] hover:shadow-xl hover:shadow-blue-500/15 hover:border-blue-500 dark:hover:border-blue-500/35 transition-all duration-300"
            >
              {/* Metric callout */}
              <div className="flex-shrink-0 w-24 text-center hidden sm:block">
                <div className={`text-2xl font-extrabold ${s.moduleColor}`}>{s.metric}</div>
                <div className="text-[10px] dark:text-slate-500 text-gray-400 leading-tight mt-0.5">{s.metricLabel}</div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-14 dark:bg-white/[0.07] bg-gray-200 flex-shrink-0" />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${s.moduleBg} ${s.moduleColor}`}>
                    {s.module}
                  </span>
                  <span className="text-[10px] dark:text-slate-500 text-gray-400 uppercase tracking-widest font-semibold">
                    → triggered by: {s.problem}
                  </span>
                </div>
                <h3 className="text-base font-bold dark:text-white text-gray-900 mb-1.5 leading-snug">{s.outcome}</h3>
                <p className="text-sm dark:text-slate-400 text-gray-500 leading-relaxed">{s.detail}</p>
              </div>

              {/* Check indicator */}
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 7l3.5 3.5 5.5-7" />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Two modules intro */}
        <div className="border-t dark:border-white/[0.07] border-gray-200 pt-14">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest dark:text-slate-500 text-gray-400 mb-3">Delivered by two focused modules</p>
            <h3 className="text-2xl sm:text-3xl font-extrabold dark:text-white text-gray-900 mb-3">
              WealthScope +{" "}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">PortfolioIQ</span>
            </h3>
            <p className="text-sm dark:text-slate-400 text-gray-500 max-w-md mx-auto">
              Two modules that share data to give you insights neither could provide alone — at $34.99/month for both.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-[780px] mx-auto">
            {[
              {
                name: "WealthScope",
                tagline: "Your financial readiness layer",
                points: ["Net worth & buying power", "Debt-to-income ratio (DTI)", "Deal Readiness Score", "AI-powered financial alerts"],
                price: "$9.99/mo",
                color: "blue",
                href: "/platform#wealthscope",
              },
              {
                name: "PortfolioIQ",
                tagline: "Your property performance layer",
                points: ["DSCR per property + portfolio", "Cap rate & cash-on-cash return", "1-click DSCR lender reports", "Schedule E tax reports"],
                price: "$29.99/mo",
                color: "emerald",
                href: "/platform#portfolioiq",
              },
            ].map((m) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`relative flex flex-col dark:bg-white/[0.04] bg-white border rounded-2xl p-6 hover:shadow-lg transition-all duration-300 group ${
                  m.color === "blue"
                    ? "dark:border-blue-500/20 border-blue-300/80 hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-blue-500/20"
                    : "dark:border-emerald-500/20 border-blue-300/80 hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-blue-500/20"
                }`}
              >
                <div className={`text-xs font-bold uppercase tracking-widest mb-1 ${m.color === "blue" ? "dark:text-blue-400 text-blue-600" : "dark:text-emerald-400 text-emerald-600"}`}>
                  {m.name}
                </div>
                <p className="text-sm dark:text-slate-400 text-gray-500 mb-4">{m.tagline}</p>
                <ul className="space-y-2 flex-1 mb-5">
                  {m.points.map(p => (
                    <li key={p} className="flex items-center gap-2 text-sm dark:text-slate-300 text-gray-600">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${m.color === "blue" ? "bg-blue-400" : "bg-emerald-400"}`} />
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold dark:text-white text-gray-900">{m.price}</span>
                  <Link to={m.href} className={`text-xs font-semibold hover:underline ${m.color === "blue" ? "dark:text-blue-400 text-blue-600" : "dark:text-emerald-400 text-emerald-600"}`}>
                    See all features →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Bundle nudge */}
          <div className="mt-6 max-w-[780px] mx-auto dark:bg-gradient-to-r dark:from-blue-900/30 dark:to-emerald-900/20 bg-gradient-to-r from-blue-50 to-emerald-50 border dark:border-white/10 border-blue-100 rounded-xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm dark:text-slate-300 text-gray-700">
              <span className="font-bold dark:text-white text-gray-900">Get both for $34.99/mo</span>{" "}
              — save $5/month vs buying separately.
            </p>
            <SleekButton
              variant="primary"
              className="shrink-0 !px-5 !py-2 !text-sm"
              onClick={() => window.open("https://app.vawena.ai/signup", "_self")}
            >
              Start Free Trial
            </SleekButton>
          </div>
        </div>
      </div>
    </section>
  );
};
